import React from "react";
import { X, ChevronLeft, GraduationCap, TrendingUp, TrendingDown, Check, AlertTriangle, Lightbulb, Clock, Activity, Link2, ListOrdered, LogOut } from "lucide-react";
import { TUTORIALS, TUTORIAL_ORDER, STRATEGY_TUTORIALS } from "@/lib/indicatorTutorials";

const Section = ({ icon: Icon, title, children, color = "text-vts-blue" }) => (
  <div className="rounded-lg border border-vts-line bg-vts-surface/40 p-3">
    <div className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider ${color}`}>
      <Icon className="h-3.5 w-3.5" /> {title}
    </div>
    <div className="mt-1.5 text-[12.5px] leading-relaxed text-vts-silver/90">{children}</div>
  </div>
);

const Bullets = ({ items }) => (
  <ul className="space-y-1">
    {items.map((it, i) => (
      <li key={i} className="flex gap-1.5"><span className="text-vts-silver/40">•</span><span>{it}</span></li>
    ))}
  </ul>
);

// A small generic schematic illustrating a buy then sell signal — reused across topics.
const ExampleChart = () => (
  <svg viewBox="0 0 280 90" className="mt-2 w-full rounded-md bg-[#0b0e14] border border-white/10" role="img" aria-label="Example chart with buy and sell signals">
    <polyline points="6,70 28,64 50,72 72,58 94,62 116,46 138,50 160,34 182,40 204,26 226,32 248,20 270,28"
      fill="none" stroke="#38bdf8" strokeWidth="2" />
    <g>
      <circle cx="50" cy="72" r="4" fill="#22c55e" />
      <text x="50" y="86" fill="#22c55e" fontSize="9" textAnchor="middle" fontFamily="monospace">BUY</text>
      <path d="M50,72 l-4,8 h8 z" fill="#22c55e" transform="rotate(180 50 76)" />
    </g>
    <g>
      <circle cx="248" cy="20" r="4" fill="#ef4444" />
      <text x="248" y="14" fill="#ef4444" fontSize="9" textAnchor="middle" fontFamily="monospace">SELL</text>
    </g>
  </svg>
);

export const TutorialSheet = ({ topic, onClose, onSelectTopic }) => {
  if (!topic) return null;
  const isIndex = topic === "index";
  const isStrat = typeof topic === "string" && topic.startsWith("strat:");
  const strat = isStrat ? STRATEGY_TUTORIALS[topic.slice(6)] : null;
  const t = (!isIndex && !isStrat) ? TUTORIALS[topic] : null;
  if (!isIndex && !t && !strat) return null;

  return (
    <>
      <div data-testid="pro-tutorial-backdrop" onClick={onClose} className="fixed inset-0 z-[75] bg-black/60 backdrop-blur-sm" />
      <div data-testid="pro-tutorial-sheet" className="fixed inset-x-0 bottom-0 z-[76] flex max-h-[88vh] flex-col rounded-t-2xl border-t border-vts-line bg-vts-card shadow-2xl lg:inset-auto lg:right-4 lg:top-16 lg:bottom-4 lg:w-[460px] lg:rounded-2xl lg:border">
        {/* header */}
        <div className="flex items-center gap-2 border-b border-vts-line p-3 shrink-0">
          {!isIndex && (
            <button data-testid="pro-tutorial-back" onClick={() => onSelectTopic("index")} className="p-1.5 rounded-lg hover:bg-vts-surface">
              <ChevronLeft className="h-5 w-5 text-vts-silver/70" />
            </button>
          )}
          <GraduationCap className="h-5 w-5 text-vts-blue" />
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-bold text-white truncate">{isIndex ? "Learn to Trade" : (strat ? strat.name : t.name)}</div>
            <div className="text-[10px] text-vts-silver/50">{isIndex ? "Built-in lessons — no YouTube needed" : (strat ? "Strategy playbook" : "Indicator tutorial")}</div>
          </div>
          <button data-testid="pro-tutorial-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-vts-surface">
            <X className="h-5 w-5 text-vts-silver/60" />
          </button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
          {isIndex ? (
            <>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-400 mb-1">Strategy Playbooks</div>
              <div className="grid grid-cols-1 gap-1.5">
                {Object.keys(STRATEGY_TUTORIALS).map((k) => (
                  <button key={k} data-testid={`pro-tutorial-strat-${k}`} onClick={() => onSelectTopic(`strat:${k}`)}
                    className="flex items-center gap-2 rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2.5 text-left hover:border-amber-500/60 hover:bg-amber-500/10 transition">
                    <ListOrdered className="h-4 w-4 text-amber-400/80 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-semibold text-white truncate">{STRATEGY_TUTORIALS[k].name}</div>
                      <div className="text-[10.5px] text-vts-silver/55 truncate">{STRATEGY_TUTORIALS[k].idea}</div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-vts-blue mt-2 mb-1">Indicators & Concepts</div>
              <div className="grid grid-cols-1 gap-1.5">
                {TUTORIAL_ORDER.map((k) => (
                  <button key={k} data-testid={`pro-tutorial-topic-${k}`} onClick={() => onSelectTopic(k)}
                    className="flex items-center gap-2 rounded-lg border border-vts-line bg-vts-surface/40 px-3 py-2.5 text-left hover:border-vts-blue/50 hover:bg-vts-blue/10 transition">
                    <GraduationCap className="h-4 w-4 text-vts-blue/70 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-semibold text-white truncate">{TUTORIALS[k].name}</div>
                      <div className="text-[10.5px] text-vts-silver/55 truncate">{TUTORIALS[k].what}</div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : strat ? (
            <>
              <Section icon={Lightbulb} title="The idea">{strat.idea}</Section>
              <Section icon={Activity} title="Indicators used">
                <div className="flex flex-wrap gap-1.5">
                  {strat.indicators.map((p) => <span key={p} className="rounded-full border border-vts-blue/40 bg-vts-blue/10 px-2 py-0.5 text-[11px] text-vts-blue">{p}</span>)}
                </div>
              </Section>
              <Section icon={ListOrdered} title="How to use it">
                <ol className="space-y-1 list-decimal pl-4">{strat.howto.map((s, i) => <li key={i}>{s}</li>)}</ol>
              </Section>
              <div className="grid grid-cols-1 gap-2">
                <Section icon={TrendingUp} title="Entry — BUY" color="text-vts-profit">{strat.buy}</Section>
                <Section icon={TrendingDown} title="Entry — SELL" color="text-vts-loss">{strat.sell}</Section>
                <Section icon={LogOut} title="Exit & stop" color="text-amber-400">{strat.exit}</Section>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Section icon={Activity} title="Best market">{strat.market}</Section>
                <Section icon={Clock} title="Best timeframe">{strat.timeframe}</Section>
              </div>
              <Section icon={Check} title="Tips" color="text-vts-profit"><Bullets items={strat.tips} /></Section>
              <Section icon={AlertTriangle} title="Common mistakes" color="text-vts-loss"><Bullets items={strat.mistakes} /></Section>
              <Section icon={Lightbulb} title="Example — buy & sell signals"><ExampleChart /></Section>
            </>
          ) : (
            <>
              <Section icon={Lightbulb} title="What it does">{t.what}</Section>
              <Section icon={Activity} title="Why traders use it">{t.why}</Section>
              <div className="grid grid-cols-1 gap-2">
                <Section icon={TrendingUp} title="When to BUY" color="text-vts-profit">{t.buy}</Section>
                <Section icon={TrendingDown} title="When to SELL" color="text-vts-loss">{t.sell}</Section>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Section icon={Clock} title="Best settings — Forex">{t.forex}</Section>
                <Section icon={Clock} title="Best settings — Volatility">{t.vi}</Section>
              </div>
              <Section icon={Clock} title="Best timeframe">{t.timeframe}</Section>
              <Section icon={Activity} title={`Works best in ${t.market} markets`}>
                This tool performs best in <span className="font-semibold text-white">{t.market}</span> conditions — match it to the market regime before trading.
              </Section>
              <div className="grid grid-cols-1 gap-2">
                <Section icon={Check} title="Advantages" color="text-vts-profit"><Bullets items={t.advantages} /></Section>
                <Section icon={AlertTriangle} title="Disadvantages" color="text-amber-400"><Bullets items={t.disadvantages} /></Section>
                <Section icon={AlertTriangle} title="Common mistakes" color="text-vts-loss"><Bullets items={t.mistakes} /></Section>
              </div>
              <Section icon={Lightbulb} title="Example — buy & sell signals">
                {t.example}
                <ExampleChart />
              </Section>
              <Section icon={Link2} title="Pairs well with">
                <div className="flex flex-wrap gap-1.5">
                  {t.pairsWith.map((p) => (
                    <span key={p} className="rounded-full border border-vts-blue/40 bg-vts-blue/10 px-2 py-0.5 text-[11px] text-vts-blue">{p}</span>
                  ))}
                </div>
              </Section>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default TutorialSheet;
